import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Job } from '../types/job';
import { AnalysisResponse, CVAnalysis } from '../types/analysis';
import Modal from '../components/common/Modal';
import { BuildingOfficeIcon, MapPinIcon, CalendarIcon, LinkIcon, 
  DocumentCheckIcon, ArrowLeftIcon, CheckCircleIcon, XCircleIcon, PencilSquareIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import CVUpload from '../components/cv/CVUpload';
import { motion, AnimatePresence } from 'framer-motion';
import ApplicationModal from '../components/applications/ApplicationModal';
import { toast } from 'sonner';
import { jobsApi } from '../services/api/jobs';
import { useAuth } from '../contexts/AuthContext';

const formatScore = (score: number | undefined): string => {
  if (score === undefined || isNaN(score)) return 'N/A';
  return `${Math.round(score * 100)}%`;
};

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<CVAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showLatexEditor, setShowLatexEditor] = useState(false);
  const [latexContent, setLatexContent] = useState('');
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [returnedFromLinkedIn, setReturnedFromLinkedIn] = useState(false);
  const { user } = useAuth();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [fullDescription, setFullDescription] = useState<string>('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  useEffect(() => {
    const checkLinkedInReturn = () => {
      const storedReturn = sessionStorage.getItem(`linkedin_return_${id}`);
      if (storedReturn) {
        setShowApplicationModal(true);
        sessionStorage.removeItem(`linkedin_return_${id}`);
      }
    };

    // Add visibility change listener
    const handleVisibilityChange = () => {
      if (!document.hidden) {  // When page becomes visible again
        checkLinkedInReturn();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    loadJobDetails();
    checkLinkedInReturn();  // Initial check

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [id]);

  const loadJobDetails = async () => {
    try {
      setLoading(true);
      if (!id) throw new Error('Job ID is required');
      const data = await jobsApi.getJobById(Number(id));
      setJob(data);
    } catch (err) {
      console.error('Failed to load job details:', err);
      setError('Failed to load job details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/jobs');
  };

  const handleApplyClick = () => {
    if (job?.url) {
      sessionStorage.setItem(`linkedin_return_${id}`, 'true');
      window.open(job.url, '_blank');
    }
  };

  const handleApplicationStatus = async (status: 'applied' | 'to_apply' | 'archived') => {
    try {
      await jobsApi.trackApplication(Number(id), user?.id || '', status);
      setShowApplicationModal(false);
      
      toast.success('Application Updated', {
        description: 'Your application status has been saved successfully.',
        duration: 4000,
        className: 'my-toast',
      });

      // if (status !== 'to_apply') {
      //   navigate('/board');
      // }
    } catch (error) {
      console.error('Failed to update application status:', error);
      toast.error('Error', {
        description: 'Failed to update application status. Please try again.',
        duration: 4000,
      });
    }
  };

  const handleAnalysis = async (file: File) => {
    if (!job?.id) {
      setError('Job information is missing');
      return;
    }
    
    try {
      setAnalyzing(true);
      setError(null);

      if (!file.type.match(/pdf|msword|officedocument/)) {
        setError('Please upload a PDF or Word document');
        return;
      }

      const result = await jobsApi.analyzeCV(file, job.id);
      setAnalysis(result as CVAnalysis);
    } catch (err: any) {
      console.error('Analysis failed:', err);
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          setError(detail.map(e => e.msg).join(', '));
        } else {
          setError(detail);
        }
      } else {
        setError(err.message || 'Failed to analyze CV');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // const handleImproveCV = async () => {
  //   if (!analysis) return;
    
  //   try {
  //     const enhancedCV = await generateEnhancedCV(analysis);
  //     setLatexContent(enhancedCV);
  //     setShowLatexEditor(true);
  //   } catch (err) {
  //     console.error('Failed to generate enhanced CV:', err);
  //     setError('Failed to generate enhanced CV. Please try again.');
  //   }
  // };

  const handleSaveCV = async (content: string) => {
    try {
      // Here you would typically send the content to your backend to compile
      setShowLatexEditor(false);
      // You could add additional logic here to show a success message
    } catch (err) {
      console.error('Failed to save CV:', err);
      setError('Failed to save CV. Please try again.');
    }
  };

  // Add handler for description update
  const handleDescriptionUpdate = () => {
    if (!fullDescription.trim()) {
      toast.error('Please paste the job description');
      return;
    }
    
    // Update the job description for ATS scoring
    setJob(prev => prev ? {
      ...prev,
      description: fullDescription
    } : null);
    
    setIsEditingDescription(false);
    toast.success('Job description updated for ATS scoring');
  };

  const renderJobDetails = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      {/* Header Section */}
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-4">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {job?.title}
            </h1>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center px-4 py-2 bg-blue-50 rounded-xl text-blue-700">
                <BuildingOfficeIcon className="w-5 h-5 mr-2" />
                <span className="font-medium">{job?.company}</span>
              </div>
              <div className="flex items-center px-4 py-2 bg-purple-50 rounded-xl text-purple-700">
                <MapPinIcon className="w-5 h-5 mr-2" />
                <span>{job?.location}</span>
              </div>
              <div className="flex items-center px-4 py-2 bg-green-50 rounded-xl text-green-700">
                <CalendarIcon className="w-5 h-5 mr-2" />
                <span>{new Date(job?.timestamp || '').toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Job Description</h2>
            <button
              onClick={() => setIsEditingDescription(!isEditingDescription)}
              className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all duration-200"
            >
              <PencilSquareIcon className="w-4 h-4 mr-2" />
              {isEditingDescription ? 'Cancel' : 'Add Full Description'}
            </button>
          </div>

          {/* Disclaimer */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-xl">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Job descriptions are added by users. JOE AI does not automatically copy or store job postings from LinkedIn or any other job site. Please ensure you comply with the original job site's terms.
                </p>
              </div>
            </div>
          </div>

          {isEditingDescription ? (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center text-blue-700 mb-3">
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">
                    Paste the full job description for better ATS scoring
                  </span>
                </div>
                <textarea
                  value={fullDescription}
                  onChange={(e) => setFullDescription(e.target.value)}
                  placeholder="Paste the complete job description here..."
                  className="w-full h-48 p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsEditingDescription(false)}
                  className="px-6 py-2.5 text-gray-600 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDescriptionUpdate}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Update Description
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="prose max-w-none text-gray-700">
                <p className="line-clamp-6 text-lg leading-relaxed">
                  {job?.description?.replace(/<[^>]*>/g, '')}
                </p>
              </div>
              
              <div className="mt-8">
                <button
                  onClick={() => window.open(job?.url, '_blank')}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 group font-medium"
                >
                  <LinkIcon className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                  View Full Description on LinkedIn
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* CV Match Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 rounded-2xl p-8 border border-blue-100 shadow-lg"
      >
        <div className="flex items-center mb-6">
          <DocumentCheckIcon className="w-7 h-7 text-blue-600 mr-3" />
          <h3 className="text-xl font-semibold text-blue-900">
            Check Your CV Match
          </h3>
        </div>
        <p className="text-blue-700 mb-6 text-lg">
          Upload your CV to see how well your skills match this position
        </p>
        <CVUpload onUpload={handleAnalysis} loading={analyzing} />
      </motion.div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-6">
        <button
          onClick={handleApplyClick}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
        >
          Apply Now
        </button>
        <button
          onClick={handleClose}
          className="flex-1 px-6 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
        >
          Close
        </button>
      </div>
    </motion.div>
  );

  const renderAnalysisResults = () => {
    if (!analysis?.analysis) return null;

    // Log the analysis structure to debug
    console.log('Analysis:', analysis);

    // Access score directly from analysis.analysis
    const score = analysis.analysis.score;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => setAnalysis(null)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-1" />
            Back to Job Details
          </button>
          <div className="text-sm text-gray-500">
            Analysis for {analysis.analysis.details.job_title}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Match Score</h2>
              <div className="text-4xl font-bold">{formatScore(score)}</div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-blue-800/30 rounded-full h-3">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.round((score || 0) * 100)}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="bg-white h-3 rounded-full"
                />
              </div>
            </div>
          </div>

          <div className="p-6 space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Experience Match</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Your Experience</span>
                  <span className="font-medium">{analysis.analysis.details.experience_analysis.cv_years} years</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Required Experience</span>
                  <span className="font-medium">{analysis.analysis.details.experience_analysis.required_years} years</span>
                </div>
                <div className="mt-3 flex items-center">
                  {analysis.analysis.details.experience_analysis.meets_requirement ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                  )}
                  <span className={analysis.analysis.details.experience_analysis.meets_requirement ? "text-green-700" : "text-red-700"}>
                    {analysis.analysis.details.experience_analysis.meets_requirement 
                      ? "Meets experience requirement" 
                      : "Does not meet experience requirement"}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Skills Match</h3>
                <span className="text-sm text-gray-500">
                  {Math.round(analysis.analysis.details.skills_analysis.match_percentage * 100)}% match
                </span>
              </div>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Matching Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {analysis.analysis.details.skills_analysis.matched_skills.map((skill, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-100 flex items-center"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1 text-blue-500" />
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </div>

              {analysis.analysis.details.skills_analysis.missing_skills.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Skills to Acquire</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.analysis.details.skills_analysis.missing_skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gray-50 text-gray-600 rounded-full text-sm border border-gray-200"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Keyword Match</h3>
                <span className="text-sm text-gray-500">
                  {Math.round(analysis.analysis.details.keyword_analysis.match_percentage * 100)}% match
                </span>
              </div>
              <div className="grid gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Matching Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.analysis.details.keyword_analysis.matched_keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {analysis.analysis.details.recommendations.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
                <div className="space-y-2">
                  {analysis.analysis.details.recommendations.map((recommendation, index) => (
                    <div
                      key={index}
                      className="bg-yellow-50 border-l-4 border-yellow-400 p-4"
                    >
                      <p className="text-yellow-700">{recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            onClick={handleApplyClick}
            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Apply Now
          </button>
          {/* <button
            onClick={handleImproveCV}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Improve CV
          </button> */}
          <button
            onClick={() => setAnalysis(null)}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Try Different CV
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      title={analysis ? "CV Analysis Results" : "Job Details"}
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-600 py-8">{error}</div>
      ) : !job ? (
        <div className="text-center text-gray-600 py-8">Job not found</div>
      ) : (
        <AnimatePresence mode="wait">
          {analysis ? renderAnalysisResults() : renderJobDetails()}
        </AnimatePresence>
      )}

      <ApplicationModal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        onApplied={() => handleApplicationStatus('applied')}
        onApplyLater={() => handleApplicationStatus('to_apply')}
        onArchive={() => handleApplicationStatus('archived')}
        jobTitle={job?.title || ''}
        company={job?.company || ''}
      />
    </Modal>
  );
};

export default JobDetailPage; 